import { createSelector } from "reselect";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import { SWATCHES_PAGE_VARIANT_NAME } from "@tSurya/Common/Utilities/swatchUtils";

/** Custom property key set by the backend on swatch order lines and swatch wish list lines. */
export const SWATCH_LINE_PROPERTY_KEY = "mgbw_isSwatch";

/** True when the current page is the dedicated swatches PLP (CMS variant "Swatches"). */
export const getIsSwatchesPlp = (state: CustomApplicationState): boolean => getCurrentPage(state).variantName === SWATCHES_PAGE_VARIANT_NAME;

/** Returns true when a cart line is a swatch line (flagged by the backend). */
const isSwatchLine = (line: CartLineModel): boolean => line.properties?.[SWATCH_LINE_PROPERTY_KEY] === "true";

/** All swatch cart lines from the current cart, memoized. */
export const getSwatchCartLines = createSelector(getCurrentCartState, cartState => {
    const cart = cartState.value;
    return cart?.cartLines?.filter(isSwatchLine) ?? [];
});

/** The number of swatch items in the cart, memoized. */
export const getSwatchCartCount = createSelector(getSwatchCartLines, swatchLines => swatchLines.length);

const normalizeProductNumber = (productNumber: string | null | undefined) => productNumber?.trim().toLowerCase() ?? "";

/** True when the cart contains a variant belonging to the supplied swatch parent. */
export const getIsSwatchProductInCart = (
    parentProductNumber: string,
    cartLines: CartLineModel[] | null | undefined,
    childProductIds: ReadonlySet<string>,
): boolean => {
    const normalizedParentProductNumber = normalizeProductNumber(parentProductNumber);

    return !!cartLines?.some(line => {
        const parentProductMatches =
            normalizedParentProductNumber !== "" && normalizeProductNumber(line.parentProductErpNumber) === normalizedParentProductNumber;
        const childProductMatches = line.productId != null && childProductIds.has(line.productId);

        return parentProductMatches || childProductMatches;
    });
};
